import {
  gql,
  NetworkStatus,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client'
import { useMemo } from 'react'

import {
  ConsolePostQueryVariables,
  ConsoleRevenueQuery,
  ExportRevenueTransactionsQuery,
  ExportRevenueTransactionsQueryVariables,
  OpenPayoutMutation,
  OpenPayoutMutationVariables,
  TransactionsConnectionsFragmentDoc,
  TransactionsQuery,
  TransactionsQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { dayjs } from '@/lib/dayjs'
import { useDownloadFile } from '@/lib/downloadFile'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { t } = useTranslation('consoleRevenue')
  const { downloadCsv } = useDownloadFile()
  const { data, loading } = useQuery<
    ConsoleRevenueQuery,
    ConsolePostQueryVariables
  >(QUERY)

  const { refetch: exportRevenueTransactions } = useQuery<
    ExportRevenueTransactionsQuery,
    ExportRevenueTransactionsQueryVariables
  >(EXPORT_REVENUE_TRANSACTIONS, {
    skip: true,
    nextFetchPolicy: 'network-only',
    fetchPolicy: 'network-only',
  })

  const [fetchTransactions, { data: histories, fetchMore, networkStatus }] =
    useLazyQuery<TransactionsQuery, TransactionsQueryVariables>(
      TRANSACTIONS,
      {}
    )

  const [openPayout, { loading: openingPayout }] = useMutation<
    OpenPayoutMutation,
    OpenPayoutMutationVariables
  >(MUTATION)

  const yearOfRevenues = useMemo<number[]>(() => {
    if (!data?.artist?.createdAt) {
      return []
    }
    const currentYear = dayjs().year()
    const diffYear = dayjs(dayjs()).diff(data.artist.createdAt, 'year')
    return (
      Array.from(Array(diffYear + 1).keys())
        .map(key => currentYear - key)
        //limit 5 years
        .slice(0, 5)
    )
  }, [data?.artist?.createdAt])

  return {
    state: {
      artistId: user?.uid ?? '',
      salesReport: data?.salesReport ?? null,
      transactions: data?.transactions ?? null,
      currentUser: user,
      yearOfRevenues,
      loading,
      loadingHistories:
        networkStatus === NetworkStatus.loading ||
        networkStatus === NetworkStatus.fetchMore,
      openingPayout,
      artist: data?.artist ?? null,
      histories: histories?.transactions ?? null,
      hasNextPageHistories: !!histories?.transactions?.pageInfo.hasNextPage,
      membership: data?.artist?.subscription?.products ?? undefined,
    },
    actions: {
      async onApply() {
        await openPayout()
      },
      async onFetchMore() {
        if (!fetchMore) {
          await fetchTransactions({
            variables: {
              pageInputDate: {
                limit: 30,
              },
            },
          })
          return
        }
        await fetchMore({
          variables: {
            pageInputDate: {
              cursor: histories?.transactions?.pageInfo.endCursor || null,
            },
          },
        })
      },
      async onChangeTab({ type }) {
        if (type === 'revenueHistory') {
          await fetchTransactions()
        }
      },
      async onExportCSV(year) {
        const { data } = await exportRevenueTransactions({ year })
        if (!data.exportRevenueTransactions.csvUrl) {
          throw new Error(t('fileNotFound', { year }))
        }
        await downloadCsv(data.exportRevenueTransactions.csvUrl)
      },
    },
  }
})

const QUERY = gql`
  query ConsoleRevenue {
    salesReport {
      totalSales
      formattedTotalSales @client
      store {
        totalSales
        formattedTotalSales @client
      }
      subscription {
        totalSales
        formattedTotalSales @client
      }
      tip {
        totalSales
        formattedTotalSales @client
      }
    }
    transactions {
      ...TransactionsConnections
    }
    artist {
      id
      paymentType
      hasArrangingDeliveryItems
      subscription {
        products {
          productId
        }
      }
      status
      createdAt
      bank {
        id
      }
      business {
        firstNameKana
        firstNameKanji
      }
    }
  }
  ${TransactionsConnectionsFragmentDoc}
`

const EXPORT_REVENUE_TRANSACTIONS = gql`
  query ExportRevenueTransactions($year: Int!) {
    exportRevenueTransactions(year: $year) {
      csvUrl
    }
  }
`

const TRANSACTIONS = gql`
  query Transactions($pageInputDate: PageInputDate) {
    transactions(pageInput: $pageInputDate) {
      ...TransactionsConnections
    }
  }
  ${TransactionsConnectionsFragmentDoc}
`

const MUTATION = gql`
  mutation OpenPayout {
    openPayout {
      id
      artistId
      status
    }
  }
`
